import React, { useCallback, useMemo } from 'react';
import { AreaClosed, Bar, Line, LinePath } from '@visx/shape';
import { curveLinear, curveMonotoneX } from '@visx/curve';
import { scaleLinear, scaleUtc } from '@visx/scale';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { bisector, extent, max } from '@visx/vendor/d3-array';
import { ParentSize } from '@visx/responsive';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { motion } from 'framer-motion';
import { AnimatedGridColumns, AnimatedGridRows } from '@visx/react-spring';
import { UTCDate } from '@date-fns/utc';
import { DisplayMode } from '../charts/display-mode-selector';
import { ScaleLinear, ScaleTime } from '@visx/vendor/d3-scale';
import { formatNumber } from '../tables/table-utils';
import { useStore } from '@/store/store';
import { generateColorMap } from '@/components/charts/graph-utils';
import { startOfDay } from 'date-fns';
import {
  ChartDataPoint,
  ChartSeries,
  StackedDataPoint,
  StackedSeries,
  StackedTooltipData,
  getValue,
  getHighlightOpacity,
  collectUniqueDates,
  createTooltipData,
  calculateBarDimensions,
  calculateValueExtent,
  findDataPointAtDate,
  formatName,
  formatDate
} from './stacked-series-chart-utils';

type Props = {
  displayMode: DisplayMode;
  chartType: 'spend' | 'delivery';
  graphData: ChartSeries[];
  plannedData: ChartDataPoint[];
  showPlanned: boolean;
  stacked: boolean;
  showBars: boolean;
};

const bisectDate = bisector<StackedDataPoint, Date>(d => d.date).left;

export function StackedSeriesChart({
  graphData,
  plannedData,
  chartType,
  displayMode,
  showPlanned,
  stacked,
  showBars
}: Props) {
  if (graphData.length === 0) return null;

  return (
    <div className="relative flex h-full w-full min-w-0 px-8">
      <div className="min-w-0 flex-1">
        <ParentSize debounceTime={50}>
          {({ width, height }) => (
            <Graph
              width={width}
              height={height}
              graphData={graphData}
              plannedData={plannedData}
              chartType={chartType}
              displayMode={displayMode}
              stacked={stacked}
              showPlanned={showPlanned}
              showBars={showBars}
            />
          )}
        </ParentSize>
      </div>
    </div>
  );
}

function transformToStackedData(
  graphData: ChartSeries[],
  chartType: 'spend' | 'delivery',
  displayMode: 'discrete' | 'cumulative'
): StackedSeries[] {
  if (graphData.length === 0) return [];

  const allDates = collectUniqueDates(graphData.map(s => s.data));
  const dateBaselines = new Map<number, number>();
  allDates.forEach(date => dateBaselines.set(date.getTime(), 0));

  const stackedSeries: StackedSeries[] = [];

  graphData.forEach(series => {
    const stackedData: StackedDataPoint[] = [];

    series.data.forEach(point => {
      const timestamp = point.date.getTime();
      const baseline = dateBaselines.get(timestamp) || 0;
      const value = getValue(point, chartType, displayMode);
      const stackTop = baseline + value;

      stackedData.push({
        date: point.date,
        seriesId: series.id,
        value,
        baseline,
        stackTop,
        originalPoint: point
      });

      dateBaselines.set(timestamp, stackTop);
    });

    stackedSeries.push({
      id: series.id,
      name: series.name,
      data: stackedData
    });
  });

  return stackedSeries;
}

function getStackedValueExtent(stackedData: StackedSeries[]): [number, number] {
  if (stackedData.length === 0) return [0, 0];
  const values = stackedData.flatMap(series => series.data.map(d => d.stackTop));
  return calculateValueExtent(values);
}

type GraphProps = Props & {
  width: number;
  height: number;
};

function Graph({
  width,
  height,
  graphData,
  plannedData,
  chartType,
  displayMode,
  stacked,
  showPlanned,
  showBars
}: GraphProps) {
  const { showTooltip, tooltipData, hideTooltip, tooltipTop, tooltipLeft } =
    useTooltip<StackedTooltipData>();

  const margin = { top: 40, right: 20, bottom: 30, left: 60 };
  const colorMap = generateColorMap(graphData);

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const actualStacked = useMemo(
    () => transformToStackedData(graphData, chartType, displayMode),
    [graphData, chartType, displayMode]
  );

  const totalPlannedLine = useMemo(() => {
    if (!showPlanned || plannedData.length === 0) return [];
    return plannedData.map(d => ({ date: d.date, value: getValue(d, chartType, displayMode) }));
  }, [plannedData, showPlanned, chartType, displayMode]);

  const dateScale = useMemo(
    () =>
      scaleUtc({
        range: [margin.left, innerWidth + margin.left],
        domain: getTimeExtent(graphData, plannedData, showPlanned)
      }),
    [graphData, plannedData, showPlanned, innerWidth, margin.left]
  );

  const dateTickValues = useMemo(() => {
    const unique = collectUniqueDates(graphData.map(s => s.data));
    if (unique.length <= 12) return unique;
    const step = Math.ceil(unique.length / 12);
    return unique.filter((_, i) => i % step === 0);
  }, [graphData]);

  const valueScale = useMemo(() => {
    const maxActual = stacked
      ? getStackedValueExtent(actualStacked)[1]
      : getValueExtent(graphData, plannedData, showPlanned, chartType, displayMode)[1];

    const maxPlanned = showPlanned && stacked ? max(totalPlannedLine.map(d => d.value)) || 0 : 0;

    const maxValue = stacked ? Math.max(maxActual, maxPlanned) : maxActual;
    const buffer = maxValue / 5;

    return scaleLinear({
      range: [innerHeight + margin.top, margin.top],
      domain: stacked
        ? [0, maxValue + buffer]
        : getValueExtent(graphData, plannedData, showPlanned, chartType, displayMode),
      nice: true
    });
  }, [
    graphData,
    plannedData,
    actualStacked,
    totalPlannedLine,
    stacked,
    showPlanned,
    chartType,
    displayMode,
    margin.top,
    innerHeight
  ]);

  const today = dateScale(startOfDay(new UTCDate()));

  const handleTooltip = useCallback(
    (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
      const { x, y } = localPoint(event) || { x: 0, y: 0 };
      const x0 = dateScale.invert(x);
      const y0 = valueScale.invert(y);

      let found = false;

      // Stacked: find the series segment under the cursor (y within baseline–stackTop)
      // Unstacked: find the nearest line series by y-distance
      if (stacked) {
        const stackPoint = findStackedPoint(actualStacked, x0, y0);
        if (stackPoint) {
          found = true;
          showTooltip({
            tooltipData: createTooltipData(
              actualStacked,
              plannedData,
              chartType,
              displayMode,
              stackPoint.point.date,
              stackPoint.series,
              stackPoint.point
            ),
            tooltipLeft: x,
            tooltipTop: y
          });
        }
      } else {
        const nearestPoint = findNearestPoint(graphData, chartType, displayMode, x0, y0);
        if (nearestPoint) {
          found = true;
          // Look up the stacked version of this point for tooltip cumulative values
          const series = actualStacked.find(s => s.id === nearestPoint.data.id)!;
          const point = series.data.find(d => d.date.getTime() === nearestPoint.d.date.getTime())!;

          showTooltip({
            tooltipData: createTooltipData(
              actualStacked,
              plannedData,
              chartType,
              displayMode,
              point.date,
              series,
              point
            ),
            tooltipLeft: x,
            tooltipTop: valueScale(nearestPoint.d.value)
          });
        }
      }

      // Fallback: if no actual series matched, snap to the nearest planned data point
      if (!found && showPlanned && plannedData.length > 0) {
        const nearestPlanned = findNearestPlannedPoint(plannedData, chartType, displayMode, x0);
        if (nearestPlanned) {
          showTooltip({
            tooltipData: createTooltipData(
              actualStacked,
              plannedData,
              chartType,
              displayMode,
              nearestPlanned.date
            ),
            tooltipLeft: x,
            tooltipTop: valueScale(nearestPlanned.value)
          });
        }
      }
    },
    [
      dateScale,
      valueScale,
      graphData,
      plannedData,
      actualStacked,
      stacked,
      showPlanned,
      showTooltip,
      chartType,
      displayMode
    ]
  );

  if (graphData.length === 0 || width < 10) return null;

  return (
    <div className="absolute">
      <svg width={width} height={height}>
        <AnimatedGridRows
          left={margin.left}
          scale={valueScale}
          width={innerWidth}
          stroke={'#eff1f3'}
          animationTrajectory={'min'}
          numTicks={10}
        />
        <AnimatedGridColumns
          top={margin.top}
          scale={dateScale}
          height={innerHeight}
          stroke={'#eff1f3'}
          animationTrajectory={'min'}
          numTicks={6}
        />

        {stacked ? (
          <>
            {showBars ? (
              <StackedBarSeries
                allSeries={actualStacked}
                dateScale={dateScale}
                valueScale={valueScale}
                colorMap={colorMap}
                marginLeft={margin.left}
                innerWidth={innerWidth}
                stacked={true}
              />
            ) : (
              actualStacked.map(series => (
                <StackedAreaSeries
                  key={series.id}
                  series={series}
                  dateScale={dateScale}
                  valueScale={valueScale}
                  color={colorMap[series.id]}
                />
              ))
            )}
            {showPlanned && (
              <PlannedLine data={totalPlannedLine} dateScale={dateScale} valueScale={valueScale} />
            )}
          </>
        ) : showBars ? (
          <>
            <StackedBarSeries
              allSeries={actualStacked}
              dateScale={dateScale}
              valueScale={valueScale}
              colorMap={colorMap}
              marginLeft={margin.left}
              innerWidth={innerWidth}
              stacked={false}
            />
            {showPlanned && (
              <PlannedLine data={totalPlannedLine} dateScale={dateScale} valueScale={valueScale} />
            )}
          </>
        ) : (
          <>
            {graphData.map(d => (
              <MediaBuySeries
                key={d.id}
                data={d}
                chartType={chartType}
                displayMode={displayMode}
                dateScale={dateScale}
                valueScale={valueScale}
                color={colorMap[d.id]}
              />
            ))}
            {showPlanned && (
              <PlannedLine data={totalPlannedLine} dateScale={dateScale} valueScale={valueScale} />
            )}
          </>
        )}

        {today && (
          <Line
            from={{ x: today, y: margin.top }}
            to={{ x: today, y: innerHeight + margin.top }}
            stroke={'#637381'}
            strokeWidth={1}
            pointerEvents="none"
          />
        )}

        <Bar
          x={margin.left}
          y={margin.top}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
          rx={14}
          onMouseMove={handleTooltip}
          onMouseLeave={() => hideTooltip()}
        />

        <AxisBottom
          top={innerHeight + margin.top}
          scale={dateScale}
          tickValues={dateTickValues}
          tickFormat={value =>
            (value as Date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC'
            })
          }
        />
        <AxisLeft
          label={`${displayMode === 'discrete' ? 'Daily' : 'Cumulative'} ${chartType === 'spend' ? 'Spend' : 'Delivery'}`}
          labelProps={{ className: 'text-xs text-gray-700' }}
          left={margin.left}
          scale={valueScale}
          tickFormat={value => (chartType === 'spend' ? `$${value}` : formatNumber(value))}
        />
      </svg>
      {tooltipData && (
        <div>
          <TooltipWithBounds
            key={Math.random()}
            top={(tooltipTop || 0) - 50}
            left={tooltipLeft || 0}
            className="z-10000 shadow-lg">
            <StackedChartTooltip
              tooltipData={tooltipData}
              colorMap={colorMap}
              chartType={chartType}
              displayMode={displayMode}
            />
          </TooltipWithBounds>
        </div>
      )}
    </div>
  );
}

type PlannedLineProps = {
  data: Array<{ date: UTCDate; value: number }>;
  dateScale: ScaleTime<number, number, never>;
  valueScale: ScaleLinear<number, number, never>;
};

function PlannedLine({ data, dateScale, valueScale }: PlannedLineProps) {
  if (data.length === 0) return null;

  return (
    <LinePath
      data={data}
      curve={curveLinear}
      x={d => dateScale(d.date) ?? 0}
      y={d => valueScale(d.value) ?? 0}>
      {({ path }) => (
        <path
          d={path(data) || ''}
          stroke="#637381"
          strokeWidth={2}
          strokeDasharray="5,5"
          fill="none"
          opacity={0.8}
        />
      )}
    </LinePath>
  );
}

type StackedAreaSeriesProps = {
  series: StackedSeries;
  dateScale: ScaleTime<number, number, never>;
  valueScale: ScaleLinear<number, number, never>;
  color: string;
  highlightedId?: string;
  isPlanned?: boolean;
};

function StackedAreaSeries({
  series,
  dateScale,
  valueScale,
  color,
  isPlanned = false
}: StackedAreaSeriesProps) {
  const highlightedGraph = useStore(state => state.highlightedGraph);
  const isHighlighted = highlightedGraph === series.id;
  const opacity = getHighlightOpacity(isHighlighted, !!highlightedGraph, 0.6, 0.8, 0.3);

  return (
    <AreaClosed
      data={series.data}
      x={d => dateScale(d.date) ?? 0}
      y={d => valueScale(d.stackTop) ?? 0}
      yScale={valueScale}
      y0={d => valueScale(d.baseline) ?? 0}
      fill={color}
      fillOpacity={opacity}
      strokeWidth={0}
      strokeDasharray={isPlanned ? '5,2' : undefined}
      curve={isPlanned ? curveLinear : curveMonotoneX}
    />
  );
}

type StackedBarSeriesProps = {
  allSeries: StackedSeries[];
  dateScale: ScaleTime<number, number, never>;
  valueScale: ScaleLinear<number, number, never>;
  colorMap: Record<string, string>;
  highlightedId?: string;
  marginLeft: number;
  innerWidth: number;
  stacked: boolean;
};

function StackedBarSeries({
  allSeries,
  dateScale,
  valueScale,
  colorMap,
  marginLeft,
  innerWidth,
  stacked
}: StackedBarSeriesProps) {
  const highlightedGraph = useStore(state => state.highlightedGraph);
  if (allSeries.length === 0) return null;
  const allDates = collectUniqueDates(allSeries.map(s => s.data));

  return (
    <>
      {allDates.map((date, dateIndex) => {
        const { x: xPosition, width: barWidth } = calculateBarDimensions(
          allDates,
          dateIndex,
          dateScale,
          marginLeft,
          innerWidth
        );

        const seriesCount = allSeries.length;
        const groupedBarWidth = stacked ? barWidth : barWidth / seriesCount;
        const groupedPadding = stacked ? 0 : 1;

        return (
          <g key={date.getTime()}>
            {allSeries.map((series, seriesIndex) => {
              const dataPoint = findDataPointAtDate(series.data, date);
              if (!dataPoint || dataPoint.value === 0) return null;
              const isHighlighted = highlightedGraph === series.id;
              const opacity = getHighlightOpacity(isHighlighted, !!highlightedGraph, 0.6, 0.8, 0.3);

              let barX: number;
              let barY: number;
              let barHeight: number;

              if (stacked) {
                const y0 = valueScale(dataPoint.baseline) ?? 0;
                const y1 = valueScale(dataPoint.stackTop) ?? 0;
                barX = xPosition;
                barY = y1;
                barHeight = y0 - y1;
              } else {
                const y0 = valueScale(0) ?? 0;
                const y1 = valueScale(dataPoint.value) ?? 0;
                barX = xPosition + seriesIndex * groupedBarWidth + groupedPadding * seriesIndex;
                barY = y1;
                barHeight = y0 - y1;
              }

              if (barHeight <= 0) return null;
              return (
                <Bar
                  key={`${date.getTime()}-${series.id}`}
                  x={barX}
                  y={barY}
                  width={Math.max(1, groupedBarWidth - groupedPadding)}
                  height={barHeight}
                  fill={colorMap[series.id]}
                  fillOpacity={opacity}
                  stroke={isHighlighted ? colorMap[series.id] : 'none'}
                />
              );
            })}
          </g>
        );
      })}
    </>
  );
}

type MediaBuySeriesProps = {
  data: ChartSeries;
  chartType: 'spend' | 'delivery';
  displayMode: 'discrete' | 'cumulative';
  dateScale: ScaleTime<number, number, never>;
  valueScale: ScaleLinear<number, number, never>;
  color: string;
};

function MediaBuySeries({
  data,
  chartType,
  displayMode,
  dateScale,
  valueScale,
  color
}: MediaBuySeriesProps) {
  const points = useMemo(
    () => data.data.map(d => ({ date: d.date, value: getValue(d, chartType, displayMode) })),
    [data.data, chartType, displayMode]
  );
  const highlightedGraph = useStore(state => state.highlightedGraph);
  const isHighlighted = highlightedGraph === data.id;

  return (
    <LinePath
      data={points}
      curve={curveMonotoneX}
      x={d => dateScale(d.date) ?? 0}
      y={d => valueScale(d.value) ?? 0}
      strokeOpacity={1}>
      {({ path }) => (
        <motion.path
          fill="none"
          initial={false}
          d={path(points) || ''}
          animate={{ d: path(points) || '' }}
          stroke={color}
          opacity={highlightedGraph == null ? 1 : isHighlighted ? 1 : 0.5}
          strokeWidth={isHighlighted ? 3 : 2}
        />
      )}
    </LinePath>
  );
}

type PlannedLinePoint = { date: UTCDate; value: number };
const bisectPlannedDate = bisector<PlannedLinePoint, Date>(d => d.date).left;

function findNearestPlannedPoint(
  plannedData: ChartDataPoint[],
  chartType: 'spend' | 'delivery',
  displayMode: 'discrete' | 'cumulative',
  x0: Date
): PlannedLinePoint | undefined {
  if (plannedData.length === 0) return undefined;
  const points = plannedData.map(d => ({
    date: d.date,
    value: getValue(d, chartType, displayMode)
  }));
  const idx = bisectPlannedDate(points, x0, 1);
  const d0 = points[idx - 1];
  const d1 = points[idx];
  if (!d0) return d1;
  if (!d1) return d0;
  return x0.valueOf() - d0.date.valueOf() > d1.date.valueOf() - x0.valueOf() ? d1 : d0;
}

function findStackedPoint(
  stackedData: StackedSeries[],
  x0: Date,
  y0: number
): { series: StackedSeries; point: StackedDataPoint } | undefined {
  if (stackedData.length === 0) return undefined;

  const firstSeries = stackedData[0];
  const xIdx = bisectDate(firstSeries.data, x0, 1);
  const d0 = firstSeries.data[xIdx - 1];
  const d1 = firstSeries.data[xIdx];

  if (!d1 || !d1.date) return undefined;

  const laterValue = x0.valueOf() - d0.date.valueOf() > d1.date.valueOf() - x0.valueOf();
  const targetIdx = laterValue ? xIdx : xIdx - 1;

  for (let i = stackedData.length - 1; i >= 0; i--) {
    const series = stackedData[i];
    const point = series.data[targetIdx];

    if (!point) continue;

    if (y0 >= point.baseline && y0 <= point.stackTop) {
      return { series, point };
    }
  }

  const closestSeries = stackedData.reduce((closest, series) => {
    const point = series.data[targetIdx];
    const closestPoint = closest.data[targetIdx];

    if (!point || !closestPoint) return closest;

    const pointMidpoint = (point.baseline + point.stackTop) / 2;
    const closestMidpoint = (closestPoint.baseline + closestPoint.stackTop) / 2;

    return Math.abs(y0 - pointMidpoint) < Math.abs(y0 - closestMidpoint) ? series : closest;
  });

  return {
    series: closestSeries,
    point: closestSeries.data[targetIdx]
  };
}

function getTimeExtent(
  graphData: ChartSeries[],
  plannedData: ChartDataPoint[],
  showPlanned: boolean
) {
  const allPoints = graphData.flatMap(d => d.data);
  const allData = showPlanned ? [...allPoints, ...plannedData] : allPoints;
  return extent(allData, d => d.date) as [Date, Date];
}

function getValueExtent(
  graphData: ChartSeries[],
  plannedData: ChartDataPoint[],
  showPlanned: boolean,
  chartType: 'spend' | 'delivery',
  displayMode: 'discrete' | 'cumulative'
): [number, number] {
  const allPoints = graphData.flatMap(d => d.data);
  const allData = showPlanned ? [...allPoints, ...plannedData] : allPoints;
  return calculateValueExtent(allData.map(d => getValue(d, chartType, displayMode)));
}

type NearestPointResult = { data: ChartSeries; d: ChartDataPoint & { value: number }; idx: number };

function findNearestPoint(
  graphData: ChartSeries[],
  chartType: 'spend' | 'delivery',
  displayMode: 'discrete' | 'cumulative',
  x0: Date,
  y0: number
): NearestPointResult | undefined {
  const bisectDatePoint = bisector<ChartDataPoint, Date>(d => d.date).left;

  const points = graphData.map(data => {
    const xIdx = bisectDatePoint(data.data, x0, 1);
    const d0 = data.data[xIdx - 1];
    const d1 = data.data[xIdx];
    if (d1 && d1.date) {
      const laterValue = x0.valueOf() - d0.date.valueOf() > d1.date.valueOf() - x0.valueOf();
      const chosen = laterValue ? d1 : d0;
      const value = getValue(chosen, chartType, displayMode);
      return { data, d: { ...chosen, value }, idx: laterValue ? xIdx : xIdx - 1 };
    }
    return null;
  });

  const validPoints = points.filter(Boolean) as NearestPointResult[];
  if (validPoints.length === 0) return undefined;

  let closest = validPoints[0];
  let smallestDiff = Math.abs(closest.d.value - y0);

  for (let i = 1; i < validPoints.length; i++) {
    const currentDiff = Math.abs(validPoints[i].d.value - y0);
    if (currentDiff < smallestDiff) {
      closest = validPoints[i];
      smallestDiff = currentDiff;
    }
  }

  return closest;
}

type StackedChartTooltipProps = {
  tooltipData: StackedTooltipData;
  colorMap: Record<string, string>;
  chartType: 'spend' | 'delivery';
  displayMode: 'discrete' | 'cumulative';
};

function StackedChartTooltip({
  tooltipData,
  colorMap,
  chartType,
  displayMode
}: StackedChartTooltipProps) {
  const { series, allSeries, plannedAtDate, plannedPoint, date, plannedOnly } = tooltipData;
  const isDiscrete = displayMode === 'discrete';
  const isSpend = chartType === 'spend';
  const fmt = isSpend ? formatCurrency : formatNumber;

  const tooltipMetrics = useMemo(() => {
    const totalActual = allSeries.reduce((sum, s) => {
      const dp = findDataPointAtDate(s.data, date);
      return sum + (dp?.value || 0);
    }, 0);

    const cumulativeActual = allSeries.reduce((sum, s) => {
      const dp = findDataPointAtDate(s.data, date);
      const p = dp?.originalPoint;
      return sum + (isSpend ? p?.cumulative_spend || 0 : p?.cumulative_units || 0);
    }, 0);

    const variance = totalActual - plannedAtDate;
    const variancePercent = plannedAtDate > 0 ? (totalActual / plannedAtDate) * 100 : 0;

    return {
      totalActual,
      cumulativeActual,
      variance,
      variancePercent
    };
  }, [allSeries, date, plannedAtDate, isSpend]);

  const plannedCumulative = plannedPoint
    ? isSpend
      ? plannedPoint.cumulative_spend
      : plannedPoint.cumulative_units
    : 0;

  if (plannedOnly) {
    return (
      <div className="flex flex-col p-3 text-sm max-w-xs">
        <div className="mb-2 font-medium text-gray-800">
          {`${isDiscrete ? 'On' : 'Through'} ${formatDate(date)}`}
        </div>
        <div className="border-t border-gray-300 pt-2">
          <TooltipRow label={isDiscrete ? 'Daily Planned' : 'Planned'} value={fmt(plannedAtDate)} />
          {isDiscrete && plannedCumulative > 0 && (
            <TooltipRow label="Cumul. Planned" value={fmt(plannedCumulative)} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-3 text-sm max-w-xs">
      <div className="mb-2 font-medium text-gray-800">
        {`${isDiscrete ? 'On' : 'Through'} ${formatDate(date)}`}
      </div>

      <div className="flex flex-col gap-1 mb-2">
        {allSeries.map(s => {
          const dataPoint = findDataPointAtDate(s.data, date);
          const isHovered = s.id === series?.id;

          return (
            <div
              key={s.id}
              className={`flex items-start gap-2 py-1 px-2 rounded ${isHovered ? 'bg-blue-50' : ''}`}>
              <div
                className="mt-1 h-3 w-3 rounded-full flex-shrink-0"
                style={{ background: colorMap[s.id] }}
              />
              <div className="flex-1 min-w-0">
                <div
                  className={`text-xs truncate ${isHovered ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                  {formatName(s.name)}
                </div>
                <div className="flex justify-between gap-3 text-xs mt-0.5">
                  <span className="text-gray-600">
                    Actual:{' '}
                    <span className="font-medium text-gray-800">{fmt(dataPoint?.value || 0)}</span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-gray-300 pt-2 mt-1">
        <TooltipRow
          label={isDiscrete ? 'Daily Actual' : 'Total Actual'}
          value={fmt(tooltipMetrics.totalActual)}
        />
        {isDiscrete && (
          <TooltipRow label="Cumul. Actual" value={fmt(tooltipMetrics.cumulativeActual)} />
        )}
        {plannedAtDate > 0 && (
          <>
            <TooltipRow
              label={isDiscrete ? 'Daily Planned' : 'Total Planned'}
              value={fmt(plannedAtDate)}
            />
            {isDiscrete && plannedCumulative > 0 && (
              <TooltipRow label="Cumul. Planned" value={fmt(plannedCumulative)} />
            )}
            <div className="flex justify-between text-sm font-medium">
              <span className="text-gray-700">Variance:</span>
              <span className={tooltipMetrics.variance >= 0 ? 'text-green-700' : 'text-red-700'}>
                {fmt(tooltipMetrics.variance)} ({tooltipMetrics.variancePercent.toFixed(0)}%)
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm font-medium mb-1">
      <span className="text-gray-700">{label}:</span>
      <span className="text-gray-900 ml-4">{value}</span>
    </div>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}
